import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, ShieldAlert, ShieldCheck, ShieldX } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api, getDeviceId, getToken } from "../lib/api";
import type {
  AdminHealth,
  AdminSessionRow,
  IpBan,
  LoginAttempt,
} from "../lib/types";

export function SettingsPage({ onSignOut }: { onSignOut: () => void }) {
  const qc = useQueryClient();

  const health = useQuery<AdminHealth>({
    queryKey: ["admin-health"],
    queryFn: () => api<AdminHealth>("/v1/admin/health-admin"),
  });

  const sessions = useQuery<AdminSessionRow[]>({
    queryKey: ["admin-sessions"],
    queryFn: () => api<AdminSessionRow[]>("/v1/admin/sessions"),
  });

  const loginLog = useQuery<LoginAttempt[]>({
    queryKey: ["admin-login-log"],
    queryFn: () => api<LoginAttempt[]>("/v1/admin/security/login-log?limit=50"),
  });

  const ipBans = useQuery<IpBan[]>({
    queryKey: ["admin-ip-bans"],
    queryFn: () => api<IpBan[]>("/v1/admin/security/ip-bans"),
  });

  const revokeSession = useMutation({
    mutationFn: (id: string) =>
      api(`/v1/admin/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-sessions"] }),
  });

  const unban = useMutation({
    mutationFn: (ip: string) =>
      api(`/v1/admin/security/ip-bans/${encodeURIComponent(ip)}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-ip-bans"] }),
  });

  const tok = getToken() ?? "";
  const last4 = tok.length >= 4 ? tok.slice(-4) : "—";
  const deviceId = getDeviceId();

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
          Settings
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          Worker configuration plus admin sessions, login log, and IP bans.
        </p>
      </header>

      {health.error && (
        <Callout title="Failed to load" variant="error">
          {(health.error as Error).message}
        </Callout>
      )}

      <Section title="Environment">
        <CardGrid>
          <KvCard
            label="Environment"
            value={health.isLoading ? "…" : (health.data?.env ?? "unknown")}
            sub={envHint(health.data?.env)}
            tone={
              health.data?.env === "production"
                ? "err"
                : health.data?.env === "staging"
                  ? "warn"
                  : undefined
            }
          />
        </CardGrid>
      </Section>

      <Section title="LLM fallback">
        <CardGrid>
          <KvCard
            label="OpenRouter"
            value={
              health.isLoading
                ? "…"
                : health.data?.openrouter_configured
                  ? "configured"
                  : "not configured"
            }
            sub={
              health.data?.openrouter_configured
                ? "fires only when all 4 free sources miss"
                : "lookups will return resolved:false on full miss"
            }
            tone={health.data?.openrouter_configured ? "ok" : undefined}
          />
          <KvCard
            label="Model"
            value={
              health.data?.openrouter_model ??
              (health.isLoading ? "…" : "default")
            }
            sub={
              health.data?.openrouter_model
                ? "OPENROUTER_MODEL var override"
                : "adapter default — anthropic/claude-haiku-4-5"
            }
          />
        </CardGrid>
      </Section>

      <Section title="This device">
        <CardGrid>
          <KvCard
            label="Session token"
            value={`••••${last4}`}
            sub="opaque session token, stored in localStorage"
          />
          <KvCard
            label="Device id"
            value={deviceId.slice(0, 8) + "…"}
            sub="persistent UUID for this browser"
          />
        </CardGrid>
      </Section>

      <Section
        title="Active sessions"
        subtitle="Sessions issued by /v1/admin/login. Revoke any device that shouldn't have access — its next request returns 401."
      >
        {sessions.error && (
          <Callout title="Failed to load sessions" variant="error">
            {(sessions.error as Error).message}
          </Callout>
        )}
        <TableRoot>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Device</TableHeaderCell>
                <TableHeaderCell>IP</TableHeaderCell>
                <TableHeaderCell>Location</TableHeaderCell>
                <TableHeaderCell>Created</TableHeaderCell>
                <TableHeaderCell>Last seen</TableHeaderCell>
                <TableHeaderCell className="text-right">Action</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              )}
              {sessions.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No active sessions.</TableCell>
                </TableRow>
              )}
              {sessions.data?.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="font-medium text-gray-900 dark:text-gray-50">
                      {s.device_label ?? "Unknown device"}
                      {s.is_current && (
                        <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          this browser
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {s.device_id.slice(0, 8)}…
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{s.ip}</TableCell>
                  <TableCell>{formatLocation(s)}</TableCell>
                  <TableCell>{formatTime(s.created_at)}</TableCell>
                  <TableCell>{formatTime(s.last_seen_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      disabled={s.is_current || revokeSession.isPending}
                      onClick={() => revokeSession.mutate(s.id)}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableRoot>
      </Section>

      <Section
        title="Login activity"
        subtitle="Six failed logins from one IP within 15 minutes triggers an automatic 1-hour ban."
      >
        {loginLog.error && (
          <Callout title="Failed to load login log" variant="error">
            {(loginLog.error as Error).message}
          </Callout>
        )}
        <TableRoot>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Outcome</TableHeaderCell>
                <TableHeaderCell>When</TableHeaderCell>
                <TableHeaderCell>IP</TableHeaderCell>
                <TableHeaderCell>Location</TableHeaderCell>
                <TableHeaderCell>Device id</TableHeaderCell>
                <TableHeaderCell>User agent</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loginLog.isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              )}
              {loginLog.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No login attempts yet.</TableCell>
                </TableRow>
              )}
              {loginLog.data?.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <OutcomeBadge outcome={a.outcome} />
                  </TableCell>
                  <TableCell>{formatTime(a.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{a.ip}</TableCell>
                  <TableCell>{formatLocation(a)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {a.device_id ? a.device_id.slice(0, 8) + "…" : "—"}
                  </TableCell>
                  <TableCell
                    className="max-w-xs truncate"
                    title={a.user_agent ?? ""}
                  >
                    {a.user_agent ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableRoot>
      </Section>

      <Section
        title="IP bans"
        subtitle="Active automatic bans. Lift early if a legitimate user got caught."
      >
        {ipBans.error && (
          <Callout title="Failed to load IP bans" variant="error">
            {(ipBans.error as Error).message}
          </Callout>
        )}
        <TableRoot>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>IP</TableHeaderCell>
                <TableHeaderCell>Reason</TableHeaderCell>
                <TableHeaderCell>Failures</TableHeaderCell>
                <TableHeaderCell>Banned at</TableHeaderCell>
                <TableHeaderCell>Expires</TableHeaderCell>
                <TableHeaderCell className="text-right">Action</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ipBans.isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              )}
              {ipBans.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No active bans.</TableCell>
                </TableRow>
              )}
              {ipBans.data?.map((b) => (
                <TableRow key={b.ip}>
                  <TableCell className="font-mono text-xs">{b.ip}</TableCell>
                  <TableCell>{b.reason}</TableCell>
                  <TableCell className="tabular-nums">
                    {b.failure_count}
                  </TableCell>
                  <TableCell>{formatTime(b.banned_at)}</TableCell>
                  <TableCell>{formatTime(b.expires_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      disabled={unban.isPending}
                      onClick={() => unban.mutate(b.ip)}
                    >
                      Unban
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableRoot>
      </Section>

      <Section title="Account">
        <Button
          variant="destructive"
          onClick={() => {
            if (!confirm("Sign out of the admin dashboard?")) return;
            onSignOut();
          }}
        >
          <LogOut size={14} strokeWidth={2.25} className="mr-1.5" />
          Sign out
        </Button>
      </Section>
    </div>
  );
}

function envHint(env: string | undefined): string {
  if (env === "production") return "you are connected to PROD — be careful";
  if (env === "staging") return "safe to test";
  if (!env) return "loading…";
  return "unknown env";
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-medium text-gray-900 dark:text-gray-50">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-500">
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

function CardGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">
      {children}
    </div>
  );
}

function KvCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "ok" | "warn" | "err";
}) {
  const accent =
    tone === "warn"
      ? "border-l-2 border-l-yellow-500"
      : tone === "err"
        ? "border-l-2 border-l-red-500"
        : tone === "ok"
          ? "border-l-2 border-l-emerald-500"
          : "";
  const valueColor =
    tone === "warn"
      ? "text-yellow-600 dark:text-yellow-400"
      : tone === "err"
        ? "text-red-600 dark:text-red-400"
        : tone === "ok"
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-gray-900 dark:text-gray-50";
  return (
    <Card className={accent}>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-500">
        {label}
      </p>
      <p
        className={`mt-2 text-2xl font-semibold tracking-tight tabular-nums ${valueColor}`}
      >
        {value}
      </p>
      {sub && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{sub}</p>
      )}
    </Card>
  );
}

function OutcomeBadge({ outcome }: { outcome: LoginAttempt["outcome"] }) {
  if (outcome === "success") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        <ShieldCheck size={12} /> Success
      </span>
    );
  }
  if (outcome === "ip_banned") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
        <ShieldX size={12} /> IP banned
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
      <ShieldAlert size={12} /> Bad token
    </span>
  );
}

function formatLocation(row: {
  city: string | null;
  region: string | null;
  country: string | null;
}): string {
  const parts = [row.city, row.region, row.country].filter(
    (s): s is string => typeof s === "string" && s.length > 0,
  );
  return parts.length > 0 ? parts.join(", ") : "—";
}

function formatTime(ms: number): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}
